import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  User,
  Mail,
  Calendar,
  Shield,
  ShieldCheck,
  Crown,
  UserCheck,
  Eye,
  Clock,
  Copy,
  Edit,
  Settings,
  CheckCircle,
  AlertCircle,
  Activity,
  Loader
} from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { formatDate, formatDateTime, getInitials } from '../lib/utils';
import { User as AdminUser } from '../types';
import { toast } from 'sonner';
import { EditUserModal } from './EditUserModal';

interface UserProfileModalProps {
  user: AdminUser | null;
  isOpen: boolean;
  onClose: () => void;
  onUserUpdate?: (updatedUser: AdminUser) => void;
}

export function UserProfileModal({ user, isOpen, onClose, onUserUpdate }: UserProfileModalProps) {
  const [showEditModal, setShowEditModal] = useState(false);
  
  if (!isOpen || !user) return null;

  // Helper functions
  const getRoleConfig = (role: string) => {
    switch (role) {
      case 'super_admin':
        return { 
          color: 'bg-indigo-100 text-indigo-800', 
          icon: Crown,
          label: 'Super Admin',
          description: 'Full system access'
        };
      case 'manager':
        return { 
          color: 'bg-purple-100 text-purple-800', 
          icon: ShieldCheck,
          label: 'Manager',
          description: 'Business operations'
        };
      case 'support':
        return { 
          color: 'bg-blue-100 text-blue-800', 
          icon: UserCheck,
          label: 'Support',
          description: 'Customer service'
        };
      case 'viewer':
        return { 
          color: 'bg-gray-100 text-gray-800', 
          icon: Eye,
          label: 'Viewer',
          description: 'Read-only access'
        };
      default:
        return { 
          color: 'bg-gray-100 text-gray-800', 
          icon: Shield,
          label: 'Unknown',
          description: 'Unknown role'
        };
    }
  };

  const getStatusConfig = (isActive: boolean) => {
    return isActive
      ? {
          color: 'bg-green-100 text-green-800',
          label: 'Active'
        }
      : {
          color: 'bg-red-100 text-red-800',
          label: 'Inactive'
        };
  };

  const getLastLoginStatus = (lastLogin: Date | undefined) => {
    if (!lastLogin) {
      return { color: 'text-gray-600', label: 'Never logged in' };
    }

    const now = new Date();
    const diffHours = (now.getTime() - lastLogin.getTime()) / (1000 * 60 * 60);
    
    if (diffHours < 24) {
      return { color: 'text-green-600', label: 'Recently active' };
    } else if (diffHours < 168) { // 7 days
      return { color: 'text-yellow-600', label: 'Active this week' };
    } else {
      return { color: 'text-red-600', label: 'Inactive' };
    }
  };

  const copyUserId = () => {
    navigator.clipboard.writeText(user.id);
    toast.success('User ID copied to clipboard');
  };

  const copyEmail = () => {
    navigator.clipboard.writeText(user.email);
    toast.success('Email copied to clipboard');
  };

  const handleEditProfile = () => {
    setShowEditModal(true);
  };

  const handleEditModalClose = () => {
    setShowEditModal(false);
  };

  const handleUserSave = async (updatedUser: AdminUser) => {
    if (onUserUpdate) {
      onUserUpdate(updatedUser);
    }
    setShowEditModal(false);
  };

  const roleConfig = getRoleConfig(user.role);
  const statusConfig = getStatusConfig(user.isActive);
  const lastLoginStatus = getLastLoginStatus(user.lastLogin);
  const RoleIcon = roleConfig.icon;

  return (
    <>
      <AnimatePresence>
        <div className="fixed inset-0 bg-black/60 backdrop-blur-md z-[10001] flex items-center justify-center p-4" style={{ margin: 0, padding: '1rem', top: 0, left: 0, right: 0, bottom: 0 }}>
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ 
              type: "spring",
              duration: 0.5,
              bounce: 0.3 
            }}
            className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden"
          >
            {/* Enhanced Header */}
            <div className="relative bg-gradient-to-r from-indigo-600 to-purple-600 p-6 flex-shrink-0">
              <div className="absolute inset-0 bg-gradient-to-r from-indigo-600/20 to-transparent"></div>
              <div className="relative flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <motion.div 
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.2, type: "spring", bounce: 0.6 }}
                    className="h-16 w-16 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center border border-white/30 text-white font-bold text-xl"
                  >
                    {getInitials(user.name)}
                  </motion.div>
                  <div>
                    <motion.h2 
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.1 }}
                      className="text-2xl font-bold text-white"
                    >
                      {user.name}
                    </motion.h2>
                    <motion.div 
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.2 }}
                      className="flex items-center gap-2 text-white/80 text-sm mt-1"
                    >
                      <div className="flex items-center gap-1">
                        <span>ID: {user.id}</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={copyUserId}
                          className="p-1 h-auto text-white/60 hover:bg-white/20 hover:text-white"
                        >
                          <Copy className="w-3 h-3" />
                        </Button>
                      </div>
                    </motion.div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.3 }}
                    className="flex items-center gap-2"
                  >
                    <Badge className={`${statusConfig.color} shadow-sm`}>
                      {statusConfig.label}
                    </Badge>
                    <Badge className={`${roleConfig.color} shadow-sm`}>
                      <RoleIcon className="w-3 h-3 mr-1" />
                      {roleConfig.label}
                    </Badge>
                  </motion.div>
                  <motion.div
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={onClose}
                      className="text-white hover:bg-white/20 h-10 w-10 rounded-full"
                    >
                      <X className="h-5 w-5" />
                    </Button>
                  </motion.div>
                </div>
              </div>
            </div>

            {/* Enhanced Content */}
            <div className="flex-1 overflow-y-auto p-8 bg-gradient-to-b from-gray-50/50 to-white">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Left Column - Basic Information */}
                <motion.div 
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.4 }}
                  className="space-y-6"
                >
                  <Card className="shadow-sm border-gray-200">
                    <CardHeader className="bg-gradient-to-r from-gray-50 to-gray-100/50 rounded-t-xl">
                      <CardTitle className="flex items-center gap-2 text-base">
                        <User className="w-4 h-4 text-indigo-600" />
                        Basic Information
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-6 space-y-4">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="text-sm font-medium text-gray-600">Full Name</label>
                          <p className="font-semibold text-gray-900">{user.name}</p>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-gray-600">Email Address</label>
                          <div className="flex items-center gap-2">
                            <p className="font-semibold text-gray-900 break-all min-w-0 flex-1">{user.email}</p>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={copyEmail}
                              className="p-1 h-auto flex-shrink-0"
                            >
                              <Copy className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                      </div>
                      
                      <Separator />
                      
                      <div>
                        <label className="text-sm font-medium text-gray-600">Role</label>
                        <div className="flex items-center gap-2 mt-1">
                          <RoleIcon className="w-4 h-4 text-gray-500" />
                          <span className="font-medium text-gray-900">{roleConfig.label}</span>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">{roleConfig.description}</p>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="shadow-sm border-gray-200">
                    <CardHeader className="bg-gradient-to-r from-gray-50 to-gray-100/50 rounded-t-xl">
                      <CardTitle className="flex items-center gap-2 text-base">
                        <Calendar className="w-4 h-4 text-indigo-600" />
                        Account Details
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-6 space-y-4">
                      <div>
                        <label className="text-sm font-medium text-gray-600">Member Since</label>
                        <p className="font-semibold text-gray-900">{formatDate(user.createdAt)}</p>
                      </div>
                      
                      {user.lastLogin && (
                        <div>
                          <label className="text-sm font-medium text-gray-600">Last Login</label>
                          <p className="font-semibold text-gray-900">{formatDateTime(user.lastLogin)}</p>
                          <p className={`text-xs mt-1 ${lastLoginStatus.color}`}>
                            {lastLoginStatus.label}
                          </p>
                        </div>
                      )}
                      
                      {(user as any).updatedAt && (
                        <div>
                          <label className="text-sm font-medium text-gray-600">Profile Updated</label>
                          <p className="font-semibold text-gray-900">{formatDateTime((user as any).updatedAt)}</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </motion.div>

                {/* Right Column - Activity & Actions */}
                <motion.div 
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.5 }}
                  className="space-y-6"
                >
                  <Card className="shadow-sm border-gray-200">
                    <CardHeader className="bg-gradient-to-r from-gray-50 to-gray-100/50 rounded-t-xl">
                      <CardTitle className="flex items-center gap-2 text-base">
                        <Activity className="w-4 h-4 text-indigo-600" />
                        Activity Status
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between p-4 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl border border-indigo-100">
                        <div className="flex items-center gap-3">
                          <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                            user.isActive ? 'bg-green-100' : 'bg-red-100'
                          }`}>
                            {user.isActive ? (
                              <CheckCircle className="w-6 h-6 text-green-600" />
                            ) : (
                              <AlertCircle className="w-6 h-6 text-red-600" />
                            )}
                          </div>
                          <div>
                            <h3 className="font-semibold text-gray-900">
                              {user.isActive ? 'Active User' : 'Inactive User'}
                            </h3>
                            <p className="text-sm text-gray-600">
                              {user.isActive 
                                ? 'User has full access to the system' 
                                : 'User access is currently disabled'
                              }
                            </p>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="shadow-sm border-gray-200">
                    <CardHeader className="bg-gradient-to-r from-gray-50 to-gray-100/50 rounded-t-xl">
                      <CardTitle className="flex items-center gap-2 text-base">
                        <Settings className="w-4 h-4 text-indigo-600" />
                        Actions
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-6 space-y-3">
                      <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                        <Button 
                          onClick={handleEditProfile}
                          className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white rounded-xl h-12 font-semibold shadow-lg shadow-indigo-600/25"
                        >
                          <Edit className="w-4 h-4 mr-2" />
                          Edit Profile
                        </Button>
                      </motion.div>
                    </CardContent>
                  </Card>
                </motion.div>
              </div>
            </div>

            {/* Enhanced Footer */}
            <div className="p-6 bg-gray-50 border-t border-gray-100 flex-shrink-0">
              <div className="flex items-center justify-end">
                <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                  <Button 
                    variant="outline" 
                    onClick={onClose}
                    className="px-6 h-11 rounded-xl font-medium"
                  >
                    Close
                  </Button>
                </motion.div>
              </div>
            </div>
          </motion.div>
        </div>
      </AnimatePresence>

      {/* Edit Modal */}
      <EditUserModal
        user={user}
        isOpen={showEditModal}
        onClose={handleEditModalClose}
        onSave={handleUserSave}
      />
    </>
  );
} 